import { RefObject, useEffect, useState } from "react";
import { computePageSize, createDebounced } from "@/lib/pageSize";

interface UsePageSizeOptions {
  rowSelector: string;
  gap: number;
  min: number;
  max: number;
  fallback: number;
}

/**
 * Items per page that fit into the referenced scroll container.
 * Measures the first row matching `rowSelector`; until a row is measured
 * the `fallback` is returned. Recomputed on container resize and when rows change.
 */
export function usePageSize(
  ref: RefObject<HTMLElement | null>,
  { rowSelector, gap, min, max, fallback }: UsePageSizeOptions,
): number {
  const [size, setSize] = useState(fallback);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let rowHeight = 0;

    const update = () => {
      const row = el.querySelector<HTMLElement>(rowSelector);
      if (row) rowHeight = row.getBoundingClientRect().height;
      setSize(
        computePageSize({
          containerHeight: el.clientHeight,
          rowHeight,
          gap,
          min,
          max,
          fallback,
        }),
      );
    };

    update();
    const debouncedUpdate = createDebounced(update, 200);
    const resizeObserver = new ResizeObserver(debouncedUpdate);
    resizeObserver.observe(el);
    const mutationObserver = new MutationObserver(() => {
      if (rowHeight === 0) update();
    });
    mutationObserver.observe(el, { childList: true, subtree: true });
    return () => {
      debouncedUpdate.cancel();
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [ref, rowSelector, gap, min, max, fallback]);

  return size;
}
